import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Upload.css';
import TerminologyModal from '../../components/TerminologyModal';

const Upload = () => {
  const navigate = useNavigate();
  const [formId, setFormId] = useState('');
  const [englishFile, setEnglishFile] = useState(null);
  const [chineseFile, setChineseFile] = useState(null);
  const [showTerminologyModal, setShowTerminologyModal] = useState(false);
  const [terminologyDict, setTerminologyDict] = useState([]);
  const [showTerminologyPrompt, setShowTerminologyPrompt] = useState(false);
  const [projectNickName, setProjectNickName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    setFormId(uuidv4());
  }, []);

  const onDropEnglish = (acceptedFiles) => {
    setEnglishFile(acceptedFiles[0]);
  };

  const onDropChinese = (acceptedFiles) => {
    setChineseFile(acceptedFiles[0]);
  };

  const onDropRejected = () => {
    toast.error('The file is too large. Please upload a file smaller than 50MB.');
  };

  const { getRootProps: getRootPropsEnglish, getInputProps: getInputPropsEnglish, isDragActive: isDragActiveEnglish } = useDropzone({
    onDrop: onDropEnglish,
    onDropRejected: onDropRejected,
    accept: {
      'application/pdf': ['.pdf'],
    },
    maxSize: 50 * 1024 * 1024, // 100MB
    multiple: false,
  });

  const { getRootProps: getRootPropsChinese, getInputProps: getInputPropsChinese, isDragActive: isDragActiveChinese } = useDropzone({
    onDrop: onDropChinese,
    onDropRejected: onDropRejected,
    accept: {
      'application/pdf': ['.pdf'],
    },
    maxSize: 50 * 1024 * 1024, // 100MB
    multiple: false,
  });

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [redirectUrl, setRedirectUrl] = useState('');

  const proceedWithUpload = async (currentTerminology = terminologyDict) => {
    setIsUploading(true);
    const formData = new FormData();
    formData.append('uuid', formId);
    formData.append('doc_name_en', englishFile.name);
    formData.append('doc_name_cn', chineseFile.name);
    formData.append('project_nickname', projectNickName);
    formData.append('terminology_dict', JSON.stringify(currentTerminology));
    formData.append('doc_en', englishFile);
    formData.append('doc_cn', chineseFile);

    try {
      const response = await axios.post('/api/v1/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
        timeout: 1200000, // 1200 seconds timeout
      });
      setConfirmationMessage(response.data.message);
      setRedirectUrl(response.data.redirect_url);
      setShowConfirmation(true);
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        toast.error('Something went wrong. Please try again or restart your browser.');
      } else {
        console.error('Error uploading files:', error);
        toast.error('Something went wrong. Please try again or restart your browser.');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleClickStart = () => {
    if (englishFile && chineseFile) {
      setShowTerminologyPrompt(true);
    }
  };

  const handleConfirmation = () => {
    setShowConfirmation(false);
    navigate(redirectUrl);
  };

  const handleSaveAndUpload = (dict) => {
    setShowTerminologyModal(false);
    setTerminologyDict(dict);
    proceedWithUpload(dict);
  }

  const handleClose = () => {
    setShowTerminologyModal(false);
  }

  return (
    <div className="upload-container">
      <ToastContainer />
      {isUploading ? (
        <div className="uploading-animation">
          <div className="spinner"></div>
          <p>Uploading, please wait... {uploadProgress}%</p>
        </div>
      ) : (
        <>
          <h2 className="upload-title">Upload Bilingual Files</h2>
          <p>Please upload the English and Chinese versions of files.</p>
          <div className="dropzone-container">
            <div {...getRootPropsEnglish({ className: `dropzone ${isDragActiveEnglish ? 'dropzone-active' : ''} ${englishFile ? 'dropzone-filled' : ''}` })}>
              <input {...getInputPropsEnglish()} />
              {englishFile ? (
                <p>{englishFile.name}</p>
              ) : (
                <p>Drag and drop <strong>English</strong> PDF here, or click to select</p>
              )}
            </div>
            <div {...getRootPropsChinese({ className: `dropzone ${isDragActiveChinese ? 'dropzone-active' : ''} ${chineseFile ? 'dropzone-filled' : ''}` })}>
              <input {...getInputPropsChinese()} />
              {chineseFile ? (
                <p>{chineseFile.name}</p>
              ) : (
                <p>Drag and drop <strong>Chinese</strong> PDF here, or click to select</p>
              )}
            </div>
          </div>

          {englishFile && chineseFile && !showConfirmation && (
            <div className="upload-btn-container">
              <p>
                Optionally, to help you better distinguish your tasks, you can give this task a name.
              </p>
              <input
                type="text"
                className="project-name-input"
                placeholder="Enter Project Name Here (e.g. Project Himalaya V1)"
                value={projectNickName}
                onChange={(e) => setProjectNickName(e.target.value)}
              />
              <button className="btn" onClick={handleClickStart}>
                Start
              </button>
            </div>
          )}
        </>
      )}
      {showTerminologyPrompt && (
        <div className="confirmation-overlay">
          <div className="confirmation-dialog">
            <p>To improve accuracy, would you like to add specific translations?</p>
            <p>For example: "technician" is usually translated as "技师、技术人员", but it is translated as "工程师" in this project.</p>
            <div className="confirmation-buttons">
              <button className="btn" onClick={() => { setShowTerminologyPrompt(false); setShowTerminologyModal(true); }}>
                Yes, edit project dictionary
              </button>
              <button className="btn-secondary" onClick={() => { setShowTerminologyPrompt(false); proceedWithUpload(); }}>
                No, start proofreading directly
              </button>
            </div>
          </div>
        </div>
      )}
      {showConfirmation && (
        <div className="confirmation-overlay">
          <div className="confirmation-dialog">
            <p>{confirmationMessage}</p>
            <button className="btn" onClick={handleConfirmation}>
              OK
            </button>
          </div>
        </div>
      )}
      <TerminologyModal show={showTerminologyModal} onClose={handleClose} onSave={handleSaveAndUpload} formId={formId} />
    </div>
  );
};

export default Upload;